import { useFrame, useThree } from '@react-three/fiber'
import {
  KeyboardShortcut,
  usePointCloudContext,
  ViewerMode,
} from 'features/pointClouds'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'

type MovementState = {
  w: boolean
  s: boolean
  a: boolean
  d: boolean
  space: boolean
  shift: boolean
}

// Constants for quaternion math
const deltaQuaternionYaw = new THREE.Quaternion()
const deltaQuaternionPitch = new THREE.Quaternion()
const worldYAxis = new THREE.Vector3(0, 1, 0) // Define world Y
const cameraXAxis = new THREE.Vector3(1, 0, 0) // Define local X

export const PointerLockNavigation: React.FC = () => {
  const { camera, gl } = useThree()
  const { viewerMode, setViewerMode, speedBoostLevel, setSpeedBoostLevel } =
    usePointCloudContext()

  const [isLocked, setIsLocked] = useState<boolean>(false)

  const accumulatedYaw = useRef(0)
  const accumulatedPitch = useRef(0)

  const movement = useRef<MovementState>({
    w: false,
    s: false,
    a: false,
    d: false,
    space: false,
    shift: false,
  })

  const velocityVector = useMemo(() => new THREE.Vector3(), [])

  const handlePointerLockChange = useCallback(() => {
    if (viewerMode === ViewerMode.POINTER_NAV) {
      setIsLocked(document.pointerLockElement === gl.domElement)
    } else {
      setIsLocked(false)
      if (document.pointerLockElement === gl.domElement) {
        document.exitPointerLock()
      }
    }
  }, [gl.domElement, setIsLocked, viewerMode])

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isLocked) return
      const sensitivity = 0.002
      accumulatedYaw.current -= event.movementX * sensitivity
      accumulatedPitch.current -= event.movementY * sensitivity
      // Optional clamping...
      const MAX_PITCH_ACCUM = Math.PI / 2 - 0.1
      accumulatedPitch.current = Math.max(
        -MAX_PITCH_ACCUM,
        Math.min(MAX_PITCH_ACCUM, accumulatedPitch.current)
      )
    },
    [isLocked]
  )

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('pointerlockchange', handlePointerLockChange)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('pointerlockchange', handlePointerLockChange)
      if (isLocked) {
        document.exitPointerLock()
      }
    }
  }, [gl.domElement, isLocked, handlePointerLockChange, handleMouseMove])

  // Handle double-click to switch back to POINTER_NAV
  useEffect(() => {
    const handleDoubleClick = () => {
      if (viewerMode !== ViewerMode.POINTER_NAV) {
        setViewerMode(ViewerMode.POINTER_NAV)
      }
    }

    gl.domElement.addEventListener('dblclick', handleDoubleClick)
    return () => {
      gl.domElement.removeEventListener('dblclick', handleDoubleClick)
    }
  }, [gl.domElement, viewerMode, setViewerMode])

  // Add click handler to request pointer lock
  useEffect(() => {
    const handleCanvasClick = () => {
      if (!isLocked && viewerMode === ViewerMode.POINTER_NAV) {
        gl.domElement.requestPointerLock()
      }
    }

    gl.domElement.addEventListener('click', handleCanvasClick)
    return () => {
      gl.domElement.removeEventListener('click', handleCanvasClick)
    }
  }, [gl.domElement, isLocked, viewerMode])

  const checkKey = (key: string, toState: boolean): boolean => {
    let handled = true
    switch (key) {
      case KeyboardShortcut.FORWARD:
        movement.current.w = toState
        break
      case KeyboardShortcut.BACKWARD:
        movement.current.s = toState
        break
      case KeyboardShortcut.LEFT:
        movement.current.a = toState
        break
      case KeyboardShortcut.RIGHT:
        movement.current.d = toState
        break
      case KeyboardShortcut.UP:
        movement.current.space = toState
        break
      case KeyboardShortcut.DOWN:
        movement.current.shift = toState
        break
      default:
        handled = false
    }
    return handled
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isLocked) return

      const keyLower = event.key.toLowerCase()
      if (keyLower === KeyboardShortcut.TOGGLE_SPEED_BOOST) {
        event.preventDefault()
        event.stopPropagation()
        setSpeedBoostLevel(((speedBoostLevel + 1) % 4) as 0 | 1 | 2 | 3)
        return
      }
      if (checkKey(keyLower, true)) {
        event.stopPropagation()
      }
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      if (!isLocked) return

      const keyLower = event.key.toLowerCase()
      if (checkKey(keyLower, false)) {
        event.stopPropagation()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [isLocked, setSpeedBoostLevel, speedBoostLevel])

  useFrame((_state, delta) => {
    // Only control camera if pointer is locked
    if (isLocked) {
      // --- Quaternion Multiplication Logic ---
      // Yaw rotation around world Y axis based on accumulated mouse X
      if (accumulatedYaw.current !== 0) {
        deltaQuaternionYaw.setFromAxisAngle(worldYAxis, accumulatedYaw.current)
        camera.quaternion.multiplyQuaternions(
          deltaQuaternionYaw,
          camera.quaternion
        ) // Pre-multiply
        accumulatedYaw.current = 0 // Reset accumulator
      }

      // Pitch rotation around camera's local X axis (1,0,0) based on accumulated mouse Y
      if (accumulatedPitch.current !== 0) {
        // Use the simple, untransformed local X axis for pitch
        deltaQuaternionPitch.setFromAxisAngle(
          cameraXAxis,
          accumulatedPitch.current
        )
        // Post-multiply for local rotation
        camera.quaternion.multiplyQuaternions(
          camera.quaternion,
          deltaQuaternionPitch
        )
        accumulatedPitch.current = 0 // Reset accumulator
      }
      // --- End Quaternion Logic ---

      const SPEEDS = [7, 20, 60, 100]
      const currentSpeed = SPEEDS[speedBoostLevel]

      velocityVector.set(0, 0, 0)

      if (movement.current.w) velocityVector.z -= 1
      if (movement.current.s) velocityVector.z += 1
      if (movement.current.a) velocityVector.x -= 1
      if (movement.current.d) velocityVector.x += 1
      if (movement.current.space) velocityVector.y += 1
      if (movement.current.shift) velocityVector.y -= 1

      if (velocityVector.length() > 0) {
        velocityVector.normalize()
        velocityVector.multiplyScalar(currentSpeed * delta)
        camera.translateX(velocityVector.x)
        camera.translateY(velocityVector.y)
        camera.translateZ(velocityVector.z)
      }
    }
  })

  return null
}

export default PointerLockNavigation
